import * as SupportServiceActions from '../actions/support-service.actions';

import {PaginationModel} from '@shared/models/pagination.model';
import {BaseNameResponse, SupportCategoryModel, SupportTicketMessageModel, SupportTicketModel, SupportTicketSearchRequest} from '@services/back-office';
import {createEntityAdapter, EntityAdapter, EntityState} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import {ConfigService} from '@config/config.service';

export const updateTicketsCountForAuthor = (ticketsState: TicketsState, newTicket: SupportTicketModel) => {
    const authorId = newTicket.author?.id;

    const updatedEntities = Object.fromEntries(
        Object.entries(ticketsState.entities).map(([id, ticket]) => {
            if (ticket?.author?.id === authorId) {
                return [id, { ...ticket, author: { ...ticket?.author, ticketsCount: newTicket.author?.ticketsCount } }];
            }
            return [id, ticket];
        })
    );

    return {
        ...ticketsState,
        entities: updatedEntities,
    };
};

export const ticketsAdapter: EntityAdapter<SupportTicketModel> = createEntityAdapter<SupportTicketModel>({
    sortComparer: (a: any, b: any) => a.lastAction.date < b.lastAction.date ? 1 : a.lastAction.date > b.lastAction.date ? -1 : 0,
});

export const messagesAdapter: EntityAdapter<SupportTicketMessageModel> = createEntityAdapter<SupportTicketMessageModel>({
    sortComparer: (a: any, b: any) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
});

export interface TicketsListFilterModel {
    types?: SupportTicketSearchRequest.TypesEnum[];
    statuses?: SupportTicketSearchRequest.StatusesEnum[];
    user?: BaseNameResponse;
    manager?: BaseNameResponse;
    organization?: BaseNameResponse;
    category?: BaseNameResponse;
    lastUpdatedByDah?: boolean;
    lastActionAgo?: SupportTicketSearchRequest.LastActionAgoEnum;
}

export interface State {
    categories: SupportCategoryModel[],
    tickets: TicketsState,
    openedTicket?: SupportTicketModel,
    messages: Record<string, MessagesState>
}

export interface TicketsState extends EntityState<SupportTicketModel> {
    filter: {
        SUPPORT: TicketsListFilterModel,
        FEATURE: TicketsListFilterModel,
    },
    pagination?: PaginationModel,
    loading?: boolean,
    itemsLoading?: string[],
    tab: 'SUPPORT' | 'FEATURE',
    ticketFromQueryId?: string,
}

export interface MessagesState extends EntityState<SupportTicketMessageModel> {
    pagination?: PaginationModel,
    listLoading?: boolean,
    messageSending?: boolean,
}

const ticketsInitialState = ticketsAdapter.getInitialState({
    loading: false,
    pagination: {
        first: true,
        last: false,
        number: 0,
        numberOfElements: 0,
        size: new ConfigService().paginationConfig.pageSize,
        totalElements: 0,
    },
    filter: {
        SUPPORT: {statuses: ['NEW' as SupportTicketModel.StatusEnum, 'IN_PROGRESS' as SupportTicketModel.StatusEnum] as any},
        FEATURE: {statuses: ['NEW' as SupportTicketModel.StatusEnum, 'IN_PROGRESS' as SupportTicketModel.StatusEnum, 'IN_DEVELOPMENT' as SupportTicketModel.StatusEnum] as any},
    },
    tab: 'SUPPORT' as any,
});

const messagesInitialState = messagesAdapter.getInitialState({
    loading: false,
    pagination: {
        first: true,
        last: false,
        number: 0,
        numberOfElements: 0,
        size: 30,
        totalElements: 0,
    },
});

export const initialState: State = {
    categories: [],
    tickets: ticketsInitialState,
    messages: {}
};

export const reducer = createReducer(
    initialState,

    on(SupportServiceActions.SetTicketFromQueryId, (state, action) => ({
        ...state,
        tickets: {
            ...state.tickets,
            ticketFromQueryId: action.ticketId,
        }
    })),


    on(SupportServiceActions.LoadCategoriesSuccess, (state, action) => ({
        ...state,
        categories: action.payload.categories,
    })),


    on(SupportServiceActions.LoadTickets, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            loading: true,
        }
    })),

    on(SupportServiceActions.LoadTicketsSuccess, (state, action) => ({
        ...state,
        tickets: ticketsAdapter.addMany(action.payload.response.content!, {
            ...state.tickets,
            pagination: {
                size: action.payload.response.size,
                last: action.payload.response.last,
                totalElements: action.payload.response.totalElements,
                numberOfElements: action.payload.response.numberOfElements,
                first: action.payload.response.first,
                number: action.payload.response.number,
            },
            loading: false,
        }),
    })),

    on(SupportServiceActions.LoadTicketsFailure, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            loading: false,
        }
    })),


    on(SupportServiceActions.SetTicketsTab, (state, action) => ({
        ...state,
        tickets: {
            ...ticketsInitialState,
            filter: state.tickets.filter,
            tab: action.payload.tab,
            ticketFromQueryId: undefined
        }
    })),

    on(SupportServiceActions.SetFilter, (state, action) => ({
        ...state,
        tickets: {
            ...ticketsInitialState,
            filter: {
                ...state.tickets.filter,
                [state.tickets.tab]: action.payload.filter
            },
            tab: state.tickets.tab,
            ticketFromQueryId: undefined
        }
    })),

    on(SupportServiceActions.ClearFilter, (state) => ({
        ...state,
        tickets: {
            ...ticketsInitialState,
            filter: {
                ...state.tickets.filter,
                [state.tickets.tab]: ticketsInitialState.filter[state.tickets.tab],
            },
            tab: state.tickets.tab,
            ticketFromQueryId: undefined
        }
    })),


    on(SupportServiceActions.SetMessagesNextPage, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...state.messages[action.payload.ticketId],
                pagination: {
                    ...state.messages[action.payload.ticketId].pagination,
                    number: state.messages[action.payload.ticketId].pagination!.number! + 1,
                }
            }
        }
    })),

    on(SupportServiceActions.OpenMessages, (state, action) => ({
        ...state,
        openedTicket: {...state.tickets.entities[action.payload.ticketId]},
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...messagesInitialState,
                ...state.messages[action.payload.ticketId],
            }
        }
    })),

    on(SupportServiceActions.LoadMessages, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...messagesInitialState,
                ...!action.payload.clearState && {...state.messages[action.payload.ticketId]},
                listLoading: true,
            }
        }
    })),

    on(SupportServiceActions.LoadMessagesSuccess, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: action.payload.clearState
                ? messagesAdapter.setMany(setImageDimsList(action.payload.response.content!), {
                    ...state.messages[action.payload.ticketId],
                    pagination: {
                        size: action.payload.response.size,
                        last: action.payload.response.last,
                        totalElements: action.payload.response.totalElements,
                        numberOfElements: action.payload.response.numberOfElements,
                        first: action.payload.response.first,
                        number: action.payload.response.number,
                    },
                    listLoading: false,
                })
                : messagesAdapter.upsertMany(setImageDimsList(action.payload.response.content!), {
                    ...state.messages[action.payload.ticketId],
                    pagination: {
                        size: action.payload.response.size,
                        last: action.payload.response.last,
                        totalElements: action.payload.response.totalElements,
                        numberOfElements: action.payload.response.numberOfElements,
                        first: action.payload.response.first,
                        number: action.payload.response.number,
                    },
                    listLoading: false,
                })
        }
    })),

    on(SupportServiceActions.LoadMessagesFailure, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...state.messages[action.payload.ticketId],
                listLoading: false,
            }
        }
    })),


    on(SupportServiceActions.SendMessage, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...messagesInitialState,
                ...state.messages[action.payload.ticketId],
                messageSending: true,
            }
        }
    })),

    on(SupportServiceActions.SendMessageSuccess, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: messagesAdapter.addOne(action.payload.response, {
                ...state.messages[action.payload.ticketId],
                messageSending: false,
            })
        }
    })),

    on(SupportServiceActions.SendMessageFailure, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...state.messages[action.payload.ticketId],
                messageSending: false,
            }
        }
    })),


    on(SupportServiceActions.ChangeStatusSuccess, (state, action) => ({
        ...state,
        tickets: ticketsAdapter.upsertOne(action.payload.response, state.tickets),
        ...state.openedTicket?.id === action.payload.response?.id && {openedTicket: action.payload.response},
    })),


    on(SupportServiceActions.SetManagerSuccess, (state, action) => ({
        ...state,
        tickets: ticketsAdapter.setOne(action.payload.response, state.tickets),
        ...state.openedTicket?.id === action.payload.response?.id && {openedTicket: action.payload.response},
    })),

    on(SupportServiceActions.AddManagerSuccess, (state, action) => ({
        ...state,
        tickets: ticketsAdapter.upsertOne(action.payload.response, state.tickets),
        ...state.openedTicket?.id === action.payload.response?.id && {openedTicket: action.payload.response},
    })),

    on(SupportServiceActions.RemoveManagerSuccess, (state, action) => ({
        ...state,
        tickets: ticketsAdapter.upsertOne(action.payload.response, state.tickets),
        ...state.openedTicket?.id === action.payload.response?.id && {openedTicket: action.payload.response},
    })),


    on(SupportServiceActions.SetNextPage, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            pagination: {
                ...state.tickets.pagination,
                number: state.tickets.pagination!.number! + 1,
            }
        }
    })),

    on(SupportServiceActions.UpsertTicket, (state, action) => {
        return {
            ...state,
            tickets: ticketsAdapter.upsertOne(action.payload.response, updateTicketsCountForAuthor(state.tickets, action.payload.response)),
            ...state.openedTicket?.id === action.payload.response.id && { openedTicket: action.payload.response },
        };
    }),

    on(SupportServiceActions.AddTicketSuccess, (state, action) => {
        return {
            ...state,
            tickets: ticketsAdapter.addOne(action.payload.response, updateTicketsCountForAuthor(state.tickets, action.payload.response)),
        };
    }),


    on(SupportServiceActions.EditTicket, (state, action) => {
        return {
            ...state,
            tickets: {
                ...state.tickets,
                itemsLoading: [...state.tickets?.itemsLoading ?? [], (action.payload.request as any).id],
            }
        };
    }),

    on(SupportServiceActions.EditTicketSuccess, (state, action) => {
        return {
            ...state,
            tickets: ticketsAdapter.setOne(action.payload.response, {
                ...state.tickets,
                itemsLoading: state.tickets?.itemsLoading?.filter(item => item !== action.payload.response.id),
            }),
        };
    }),

    on(SupportServiceActions.EditTicketFailure, (state, action) => {
        return {
            ...state,
            tickets: {
                ...state.tickets,
                itemsLoading: state.tickets?.itemsLoading?.filter(item => item !== action.payload.ticketId),
            }
        };
    }),


    on(SupportServiceActions.DeleteTicket, (state, action) => ({
        ...state,
        tickets: {
            ...state.tickets,
            itemsLoading: state.tickets?.itemsLoading ? [...state.tickets.itemsLoading, action.payload.ticketId] : [action.payload.ticketId],
        },
    })),

    on(SupportServiceActions.DeleteTicketSuccess, (state, action) => {
        const deletedTicket = state.tickets.entities[action.payload.ticketId];
        const authorId = deletedTicket?.author?.id;

        const updatedEntities = Object.fromEntries(
            Object.entries(state.tickets.entities).map(([id, ticket]) => {
                if (ticket?.author?.id === authorId) {
                    return [id, {
                        ...ticket,
                        author: {
                            ...ticket?.author,
                            ticketsCount: Math.max((ticket?.author?.ticketsCount ?? 1) - 1, 0)
                        }
                    }];
                }
                return [id, ticket];
            })
        );

        const ticketsWithUpdatedAuthor = {
            ...state.tickets,
            entities: updatedEntities,
            itemsLoading: state.tickets?.itemsLoading?.filter(item => item !== action.payload.ticketId) ?? [],
        };

        return {
            ...state,
            tickets: ticketsAdapter.removeOne(action.payload.ticketId, ticketsWithUpdatedAuthor),
            ...state.openedTicket?.id === action.payload.ticketId && {openedTicket: undefined},
        };
    }),


    on(SupportServiceActions.DeleteTicketFailure, (state, action) => ({
        ...state,
        tickets: {
            ...state.tickets,
            itemsLoading: state.tickets?.itemsLoading?.filter(item => item !== action.payload.ticketId) ?? [],
        },
    })),
);

export const {selectAll: ticketsSelector} = ticketsAdapter.getSelectors();

export const {selectAll: messagesSelector} = messagesAdapter.getSelectors();


export const setImageDimsList = (messages: SupportTicketMessageModel[]): SupportTicketMessageModel[] => {
    const result: SupportTicketMessageModel[] = [];

    for (let i = 0; i < messages.length; i++) {
        if (messages[i]) {
            result.push(setImageDims(messages[i]));
        } else {
            result.push(messages[i]);
        }
    }

    return result;
};

export const setImageDims = (message: SupportTicketMessageModel): SupportTicketMessageModel => {
    const MAX_IMAGE_WIDTH = 450;

    if (message.attachments?.[0] && message.attachments[0].width && message.attachments[0].height) {
        const maxSide = message.attachments[0].width > message.attachments[0].height ? message.attachments[0].width : message.attachments[0].height;
        const multiplier = maxSide > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH : maxSide;

        return {
            ...message,
            attachments: [{
                ...message.attachments[0],
                width: message.attachments[0].width / maxSide * multiplier,
                height: message.attachments[0].height / maxSide * multiplier
            }]
        };
    }

    return message;
};

// export const isAvailableByFilter = (ticket: SupportTicketModel, filter?: TicketsListFilterModel) => {
//     if (!filter) {
//         return true;
//     }
//
//     if (filter.types?.length !== undefined && !filter.types.includes(ticket.type!)) {
//         return false;
//     }
//
//     if (filter.statuses?.length !== undefined && !filter.statuses.includes(ticket.status!)) {
//         return false;
//     }
//
//     if (filter.user?.id !== undefined && filter.user?.id !== ticket.author?.id) {
//         return false;
//     }
//
//     if (filter.manager?.id !== undefined && filter.manager?.id !== ticket.manager?.id && !ticket.managers?.map(item => item.id)?.includes(filter.manager?.id)) {
//         return false;
//     }
//
//     if (filter.category?.id !== undefined && filter.category?.id !== (ticket.subCategory?.id ?? ticket.category?.id)) {
//         return false;
//     }
//
//     if (filter.lastUpdatedByDah !== undefined && filter.lastUpdatedByDah !== ticket.lastAction?.dah) {
//         return false;
//     }
//
//     if (filter.lastActionAgo !== undefined && filter.lastActionAgo !== ticket.lastAction?.lastActionAgo) {
//         return false;
//     }
//
//     return true;
// };
